import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Form, Button, Alert, Nav } from 'react-bootstrap';
import { useAuth } from '../contexts/AuthContext';
import { ROLES } from '../contexts/AuthContext';
import { COUNTRY_ISD_CODES, getPhoneRuleByIsd } from '../constants/countryIsdCodes';

const Login = () => {
  const [loginMode, setLoginMode] = useState('userid-password');
  const [userId, setUserId] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [countryIsdCode, setCountryIsdCode] = useState('+91');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [error, setError] = useState('');
  const [sessionMessage, setSessionMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const { loginWithUserIdPassword, loginWithEmailPassword, requestPhoneLoginOtp, verifyPhoneLoginOtp } = useAuth();
  const navigate = useNavigate();
  const phoneRule = getPhoneRuleByIsd(countryIsdCode);

  useEffect(() => {
    const redirectReason = sessionStorage.getItem('authRedirectReason');
    if (redirectReason === 'session-expired') {
      setSessionMessage('Your session has expired. Please log in again.');
    }
    sessionStorage.removeItem('authRedirectReason');
  }, []);

  const toErrorText = (value) => {
    if (typeof value === 'string') return value;
    if (Array.isArray(value)) {
      const messages = value
        .map((entry) => {
          if (typeof entry === 'string') return entry;
          if (entry && typeof entry.msg === 'string') return entry.msg;
          return null;
        })
        .filter(Boolean);
      return messages.length > 0 ? messages.join(', ') : 'Login failed';
    }
    if (value && typeof value === 'object') {
      if (typeof value.msg === 'string') return value.msg;
      if (typeof value.message === 'string') return value.message;
    }
    return 'Login failed';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    setLoading(true);

    let result;
    if (loginMode === 'userid-password') {
      result = await loginWithUserIdPassword(userId.trim().toLowerCase(), password);
    } else if (loginMode === 'email-password') {
      result = await loginWithEmailPassword(email.trim(), password);
    } else {
      if (!otpSent) {
        const cleanPhone = phoneNumber.replace(/\D/g, '');
        if (cleanPhone.length < phoneRule.minLength || cleanPhone.length > phoneRule.maxLength) {
          setError(`Enter a valid mobile number for ${countryIsdCode}`);
          setLoading(false);
          return;
        }
        result = await requestPhoneLoginOtp(countryIsdCode, cleanPhone);
        if (result.success) {
          setOtpSent(true);
          setSuccessMessage('OTP sent to your WhatsApp number');
        }
        setLoading(false);
        if (!result.success) {
          setError(toErrorText(result.message));
        }
        return;
      }
      const cleanPhone = phoneNumber.replace(/\D/g, '');
      result = await verifyPhoneLoginOtp(countryIsdCode, cleanPhone, otp.trim());
    }

    if (result.success) {
      const loggedInUser = result.user;
      const roles = Array.isArray(loggedInUser?.roles) && loggedInUser.roles.length > 0
        ? loggedInUser.roles
        : (loggedInUser?.role ? [loggedInUser.role] : []);
      if (roles.includes(ROLES.CASHIER)) {
        navigate('/pos');
      } else if (loggedInUser?.role === ROLES.BUYER && loggedInUser?.email_verified !== true) {
        navigate('/buyer/verify-email');
      } else {
        navigate('/');
      }
    } else {
      setError(toErrorText(result.message));
    }

    setLoading(false);
  };

  return (
    <Container className="py-5">
      <Row className="justify-content-center">
        <Col md={6} lg={4}>
          <Card>
            <Card.Header className="text-center">
              <h3>Login</h3>
            </Card.Header>
            <Card.Body>
              <Nav
                variant="tabs"
                activeKey={loginMode}
                className="mb-3"
                onSelect={(selectedKey) => {
                  setLoginMode(selectedKey || 'userid-password');
                  setError('');
                  setSuccessMessage('');
                  setOtpSent(false);
                  setOtp('');
                }}
              >
                <Nav.Item>
                  <Nav.Link eventKey="userid-password">User ID + Password</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="phone-otp">Phone + OTP</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="email-password">Email + Password</Nav.Link>
                </Nav.Item>
              </Nav>

              {loginMode === 'userid-password' && (
                <p className="text-muted small mb-3">
                  Use this if you remember your User ID and account password.
                </p>
              )}
              {loginMode === 'phone-otp' && (
                <p className="text-muted small mb-3">
                  Use this for quick sign-in via WhatsApp OTP on your registered phone.
                </p>
              )}
              {loginMode === 'email-password' && (
                <p className="text-muted small mb-3">
                  Use this if you registered your email and prefer email/password login.
                </p>
              )}

              <Form onSubmit={handleSubmit}>
                {loginMode === 'userid-password' && (
                  <>
                    <Form.Group className="mb-3">
                      <Form.Label>User ID</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Enter your user ID"
                        value={userId}
                        onChange={(e) => setUserId(e.target.value.replace(/[^a-zA-Z0-9]/g, '').toLowerCase())}
                        required
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Password</Form.Label>
                      <Form.Control
                        type="password"
                        placeholder="Enter your password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                      />
                    </Form.Group>
                  </>
                )}

                {loginMode === 'email-password' && (
                  <>
                    <Form.Group className="mb-3">
                      <Form.Label>Email Address</Form.Label>
                      <Form.Control
                        type="email"
                        placeholder="Enter your email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Password</Form.Label>
                      <Form.Control
                        type="password"
                        placeholder="Enter your password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                      />
                    </Form.Group>
                  </>
                )}

                {loginMode === 'phone-otp' && (
                  <>
                    <Form.Group className="mb-3">
                      <Form.Label>Country ISD Code</Form.Label>
                      <Form.Select
                        value={countryIsdCode}
                        onChange={(e) => setCountryIsdCode(e.target.value)}
                        required
                      >
                        {COUNTRY_ISD_CODES.map((entry) => (
                          <option key={entry.code} value={entry.code}>
                            {entry.label}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Mobile Number</Form.Label>
                      <Form.Control
                        type="tel"
                        placeholder="Enter your mobile number"
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, '').slice(0, phoneRule.maxLength))}
                        required
                      />
                    </Form.Group>

                    {otpSent && (
                      <Form.Group className="mb-3">
                        <Form.Label>OTP</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Enter OTP"
                          value={otp}
                          onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                          required
                        />
                      </Form.Group>
                    )}
                  </>
                )}

                {error && (
                  <Alert variant="danger" className="mb-3">
                    {error}
                  </Alert>
                )}

                {successMessage && (
                  <Alert variant="success" className="mb-3">
                    {successMessage}
                  </Alert>
                )}

                {sessionMessage && (
                  <Alert
                    variant="warning"
                    className="mb-3"
                    dismissible
                    onClose={() => setSessionMessage('')}
                  >
                    {sessionMessage}
                  </Alert>
                )}

                <div className="d-grid">
                  <Button
                    variant="primary"
                    type="submit"
                    disabled={loading}
                    size="lg"
                  >
                    {loading
                      ? (loginMode === 'phone-otp' && !otpSent ? 'Sending OTP...' : 'Logging in...')
                      : (loginMode === 'phone-otp' && !otpSent ? 'Send OTP' : 'Login')}
                  </Button>
                </div>
              </Form>
            </Card.Body>
            <Card.Footer className="text-center">
              <p className="mb-2">
                <Link to="/forgot-password" className="text-decoration-none">
                  Forgot Password?
                </Link>
              </p>
              <p className="mb-2">
                <Link to="/forgot-user-id" className="text-decoration-none">
                  Forgot User ID?
                </Link>
              </p>
              <p className="mb-0">
                Don't have an account?{' '}
                <Link to="/register" className="text-decoration-none">
                  Register here
                </Link>
              </p>
            </Card.Footer>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default Login;